import {
    ReactElement,
    useEffect,
    useState,
} from 'react';

import {
    SimpleGrid,
    Table,
} from '@mantine/core';

import {useSubscriptionItems} from '../hooks/useSubscriptionItems.ts';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';
import {
    ISubscriptionItemTableColumn,
    SubscriptionItem,
} from '../types';
import {useParams} from "react-router-dom";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {getColumns} from '../tables/columns';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/SubscriptionItemsService';
import {useQueryClient} from '@tanstack/react-query';
import {PaginationControls} from '../../../components/PaginationControls';

interface RecordsTableProps {
    selectedRecords?: SubscriptionItem[];
    setSelectedRecords?: (records: SubscriptionItem[]) => void;
    extraTools?: ReactElement[];
    settings?: { label: string, onClick: () => void }[];
}

export const RecordsTable = ({
                                 selectedRecords = [],
                                 setSelectedRecords,
                                 extraTools,
                                 settings,
                             }: RecordsTableProps) => {

        const {allowedActions} = useVerifiedPermissions()
        const queryClient = useQueryClient();

        const {id: customerId} = useParams();

        if (typeof customerId === 'undefined') {
            throw new Error('customerId is required parameter')
        }

        const {
            visibleItems,
            loading,
            fetchRecords,
            handleFilterByKeyword,
            currentPage,
            hasNext,
            hasPrevious,
            goToNextPage,
            goToPreviousPage,
        } = useSubscriptionItems(customerId);

        const [drawerOpened, setDrawerOpened] = useState(false);
        const [selectedRecord, setSelectedRecord] = useState<SubscriptionItem | null>(null);

        useEffect(() => {
            fetchRecords();
        }, []);

        const selection: Record<string, SubscriptionItem> = {};
        for (const record of selectedRecords) {
            selection[record.id] = record;
        }

        const handleRowSelection = (row: { id: string }) => {
            if (!setSelectedRecords) return;
            if (selection[row.id]) {
                setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
            } else {
                const fullRecord = visibleItems.find(item => item.id === row.id);
                if (fullRecord) {
                    setSelectedRecords([...selectedRecords, fullRecord]);
                }
            }
        };

        const handleSelectAllRows = () => {
            if (!setSelectedRecords) return;
            if (selectedRecords.length === visibleItems.length) {
                setSelectedRecords([]);
            } else {
                setSelectedRecords([...visibleItems]);
            }
        };

        const handleRecordClick = (record: SubscriptionItem) => {
            setSelectedRecord(record);
            setDrawerOpened(true);
        };

        const handleDrawerSubmit = async (values: SubscriptionItem) => {
            if (selectedRecord?.id) {
                const payload = {...values} as Record<string, unknown>;
                delete payload.created;
                delete payload.updated;
                await update(payload as SubscriptionItem);
                queryClient.invalidateQueries({queryKey: ['subscriptionItems', customerId]});
            }
        };

        const tableColumns = getColumns(handleRecordClick);

        const getColumnsWithSelection = () => {
            let cols: ISubscriptionItemTableColumn[] = [];
            if (allowedActions.includes(rbacActions.DeleteSubscriptionItem)) {

                cols.push({
                              id: 'select',
                              title: '',
                              render: item => {
                                  return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                              },
                          },
                );
            }

            cols = cols.concat(tableColumns);

            return cols;

        };


        return (
            <SimpleGrid verticalSpacing='xl' m='sm'>
                <SimpleTableBatchAction
                    limit={10}
                    handleItemsPerPageChange={() => {}}
                    handleFilterByKeyword={handleFilterByKeyword}
                    settings={settings}
                    extraTools={extraTools}
                />
                <Table verticalSpacing='sm' striped highlightOnHover>
                    <TableHeaderRow
                        columns={getColumnsWithSelection() as TableHeaderColumn[]}
                        handleSelectAllRows={handleSelectAllRows}
                    />
                    <TableBody
                        items={visibleItems}
                        loading={loading}
                        selection={selection}
                        columns={getColumnsWithSelection() as TableBodyColumn[]}
                    />
                </Table>

                <PaginationControls
                    currentPage={currentPage}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    onNext={goToNextPage}
                    onPrevious={goToPreviousPage}
                    loading={loading}
                />

                <FormDrawer
                    record={selectedRecord || {} as SubscriptionItem}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => setDrawerOpened(false)}
                />
            </SimpleGrid>
        );
    }
;
